import omnichannelChatWidgetResources from '@travel-frontend/omnichannel-chat-widget/locale/de-DE.json';
import {locale} from '@travel-frontend/ui-components';
import {use} from 'i18next';
import intervalPlural from 'i18next-intervalplural-postprocessor';
import {initReactI18next} from 'react-i18next';

import {formatCountForCta} from '@/utils/formatCountForCta';
import {getFileNameWithoutExtension} from '@/utils/fs';
import {numberFormat, percentFormat, priceFormat} from '@/utils/numberFormat';

// https://vite.dev/guide/features#glob-import
const RESOURCES_DE_DE = import.meta.glob('./locale/de-DE/*.json', {
  eager: true,
  import: 'default', // reduces bundle size
});
const RESOURCES_CS_CZ = import.meta.glob('./locale/cs-CZ/*.json', {
  eager: true,
  import: 'default',
});
const RESOURCES_SK_SK = import.meta.glob('./locale/sk-SK/*.json', {
  eager: true,
  import: 'default',
});
const RESOURCES_HU_HU = import.meta.glob('./locale/hu-HU/*.json', {
  eager: true,
  import: 'default',
});
const RESOURCES_PL_PL = import.meta.glob('./locale/pl-PL/*.json', {
  eager: true,
  import: 'default',
});

let RESOURCES: Record<string, any> = {};
if (
  import.meta.env.VITE_LOCALE === 'de-DE' ||
  import.meta.env.VITE_LOCALE === 'de-AT' ||
  import.meta.env.VITE_LOCALE === 'de-CH'
) {
  RESOURCES = RESOURCES_DE_DE;
} else if (import.meta.env.VITE_LOCALE === 'cs-CZ') {
  RESOURCES = RESOURCES_CS_CZ;
} else if (import.meta.env.VITE_LOCALE === 'sk-SK') {
  RESOURCES = RESOURCES_SK_SK;
} else if (import.meta.env.VITE_LOCALE === 'hu-HU') {
  RESOURCES = RESOURCES_HU_HU;
} else if (import.meta.env.VITE_LOCALE === 'pl-PL') {
  RESOURCES = RESOURCES_PL_PL;
}

const sharedResources = locale[import.meta.env.VITE_LOCALE];
const appSpecificResources = Object.fromEntries(
  Object.keys(RESOURCES).map((path) => [
    getFileNameWithoutExtension(path),
    RESOURCES[path],
  ])
);

const i18n = use(intervalPlural).use(initReactI18next); // passes i18n down to react-i18next

i18n.init({
  lng: import.meta.env.VITE_LOCALE,
  defaultNS: 'general',
  resources: {
    [import.meta.env.VITE_LOCALE]: {
      ...sharedResources,
      ...appSpecificResources,
      livechat: omnichannelChatWidgetResources,
    },
  },
  interpolation: {
    // react already safes from xss
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

i18n.services.formatter?.add('currency', (value, _, options) => {
  return typeof value === 'number'
    ? priceFormat(
        {amount: value, currency: options.currency},
        {
          locale: options.locale,
          fractionDigits: options.fractionDigits,
          signDisplay: options.signDisplay,
        }
      )
    : value;
});

i18n.services.formatter?.add('currency_short', (value, _, options) => {
  return typeof value === 'number'
    ? priceFormat(
        {amount: value, currency: options.currency},
        {
          locale: options.locale,
          fractionDigits: 0,
          signDisplay: options.signDisplay,
        }
      )
    : value;
});

i18n.services.formatter?.add('lowercase', (value) => value.toLowerCase());

i18n.services.formatter?.add('number', (value) => {
  return typeof value === 'number' ? numberFormat(value) : value;
});

i18n.services.formatter?.add('percentage', (value, _, options) => {
  if (typeof value === 'string') {
    const parsedNumber = Number.parseFloat(value);

    if (Number.isNaN(parsedNumber)) {
      return value;
    }

    return percentFormat(parsedNumber, options);
  }

  return typeof value === 'number' ? percentFormat(value, options) : value;
});

i18n.services.formatter?.add('hotel_count', (value) => {
  return typeof value === 'number' ? formatCountForCta(value) : value;
});

i18n.services.formatter?.add('list_plain', (value) => {
  return Array.isArray(value) ? value.join(', ') : value;
});

i18n.services.formatter?.add('list_ampersand', (value) => {
  return Array.isArray(value)
    ? value.reduce(
        (accumulator, item, index) =>
          [accumulator, item]
            .filter(Boolean)
            .join(index === value.length - 1 ? ' & ' : ', '),
        ''
      )
    : value;
});

export default i18n;
